// src/pages/qbo/qboTypes.ts
// Shapes returned by the agent's QBO module (3.17.0 review / push / conversion).
// ASP.NET serialises these classes camelCase.

export interface ReviewRow {
  key: string;
  type: string;                 // Invoice | CreditMemo | Payment | Bill | Customer | Vendor
  date: string;
  date2?: string | null;        // Invoice: due date · Payment: cash date (Cdate)
  date3?: string | null;        // Payment: deposit date
  code: number;
  name: string;
  amount: number;
  balance?: number | null;      // Invoice: balance now · Payment: total applied · Conversion: open at cutoff
  cost?: number | null;         // Invoice: ADS cost · Payment: discount taken · Conversion: paid before cutoff
  ref?: string | null;          // Invoice: terms · Payment: check text
  lines: number;
  alreadySynced: boolean;
  qboId?: string | null;
  syncAction?: string | null;
  syncStatus?: string | null;
  lastError?: string | null;
  customerLinked: boolean;
  warnings: string[];
  modified?: boolean;           // 3.19.4: changed in Advantage since the push — push again to update
  voided?: boolean;             // 3.19.4: voided in Advantage after the push
}

export interface ReviewResult {
  entity: string;
  from: string;
  to: string;
  count: number;
  alreadySynced: number;
  total: number;
  rows: ReviewRow[];
  notes: string[];
}

export interface SyncDetail {
  status: string;               // success | error | skipped | re-converted (3.19.7 stale stub rewritten)
  was?: number;                 // re-converted: balance the stub carried before
  delta?: number;               // re-converted: difference booked in the new offset
  action?: string;
  qboId?: string;
  error?: string;
  invoice?: string;
  receipt?: string;
  poNo?: string;
  code?: string;
  name?: string;
  customer?: string | number;
  type?: string;
  total?: number;
  amount?: number;
  open?: number;
  offset?: string;
  docNumber?: string;
  date?: string;
  documents?: number;
  cogsJournalId?: string | null;
  warnings?: string[];
  customerName?: string;        // 3.23.0 re-sync: customer the line belongs to
  note?: string;                // status 'info' lines
}

export interface SyncResult {
  created: number;
  updated: number;
  errors: number;
  skipped: number;
  details: SyncDetail[];
  duration?: string;
  timestamp?: string;
}

export interface ConversionDay {
  date: string;
  count: number;
  invoices: number;
  credits: number;
  openTotal: number;
  converted: number;
  uncovered?: number;        // 3.18.9: converted docs no offset journal covers yet (interrupted run)
  uncoveredOpen?: number;
  stale?: number;            // 3.19.7: converted stubs whose open balance at the cutoff changed (cash on a day that became an exception)
  staleDelta?: number;
  offsets: { key: string; qboId: string; status: string; error: string }[];
}

export interface ConversionReview {
  cutoff: string;
  bookCloseDate?: string | null;
  placeholderFound: boolean;
  placeholderCustomerId?: string | null;
  count: number;
  converted: number;
  openTotal: number;
  openInvoices: number;
  openCredits: number;
  days: ConversionDay[];
  rows: ReviewRow[];
  notes: string[];
}

export interface AccountPick {
  configured: string;
  resolved?: string | null;
  qboId?: string | null;
  type?: string | null;
  found: boolean;
}

export interface ConversionConfig {
  cutoff?: string | null;
  cutoffExceptions?: string[];          // 3.18.7: days on/before the cutoff with no legacy journal (pushed in full, not converted)
  cutoffExceptionReceipts?: boolean;
  cutoffExceptionsInvalid?: string[];   // 3.18.15: entries the agent could not read as yyyy-MM-dd
  bookCloseDate?: string | null;
  postCogsJournals: boolean;
  useItems: boolean;
  paymentDateField: string;
  arPlaceholderCustomer: { name: string; qboId?: string | null; found: boolean };
  accounts: {
    sales: AccountPick;
    freight: AccountPick;
    discount: AccountPick;
    cogs: AccountPick;
    inventoryAsset: AccountPick;
    accountsReceivable: AccountPick;
  };
}

// 3.18.7 — snapshots (agent QboSnapshotService)
export interface SnapshotSummary {
  id: string;
  label: string;
  takenAt: string;
  placeholderBalance: number;
  arTotal: number;
  customersWithBalance: number;
  customersTotal: number;
  pnlColumns: number;
  warnings: string[];
}

export interface SnapshotCompare {
  a: { id: string; label: string; takenAt: string };
  b: { id: string; label: string; takenAt: string };
  placeholder: { name: string; before: number; after: number; delta: number };
  arTotal: { before: number; after: number; delta: number };
  arAccounts: { id: string; name: string; before: number; after: number; delta: number }[];
  customers: { changedCount: number; deltaSum: number; changed: { id: string; name: string; before: number; after: number; delta: number }[] };
  pnl: { changedCount: number; columnsCompared: number; changed: { row: string; column: string; before: number; after: number; delta: number }[] };
  checks: { name: string; ok: boolean; detail: string }[];
  warnings: string[];
  note: string;
}

// 3.18.11 — one customer, QuickBooks side vs Advantage side
export interface CustomerCheck {
  code: number;
  name: string;
  adsBalanceNow: number;
  cutoff: string;
  qbo: {
    id?: string | null; name: string; balance: number; openInvoices: number; openCredits: number; unappliedPayments: number; documentsSum: number; unexplained: number;
    docs: { type: string; id?: string; docNumber?: string; date?: string; total: number; balance: number; note?: string }[];
  };
  ads: {
    openAtCutoff: number; converted: number; existingInQbo: number; remaining: number;
    docs: { invoice: string; date: string; total: number; open: number; state: string; qboId?: string | null }[];
  };
  notes: string[];
}

// 3.18.12 — Desktop-migration lumps on named customers
export interface LumpItem { type: string; date: string; num: string; amount: number; open: number }
export interface LumpsReview {
  asOf: string;
  placeholder?: { qboId: string; name: string; balance: number; documents: number; lump: number } | null;
  customersWithBalance: number;
  customersWithLump: number;
  lumpTotal: number;
  documentsTotal: number;
  balanceTotal: number;
  byType: { type: string; items: number; open: number }[];
  byYear: { year: string; items: number; open: number }[];
  byDate: { date: string; items: number; open: number }[];
  customers: { qboId: string; name: string; balance: number; documents: number; lump: number; items: number; sample: LumpItem[] }[];
  // 3.18.13: the open QuickBooks documents matched against Advantage by number
  documents?: {
    total: number; withNumber: number; noNumberOpen: number;
    classes: { cls: string; items: number; open: number }[];
    byYear: { year: string; items: number; open: number; paidInAdvantage: number; openInAdvantage: number; notInAdvantage: number }[];
    customers: { qboId: string; name: string; balance: number; documents: number; items: number; paidInAdvantage: number; openInAdvantage: number; notInAdvantage: number; oldest: string; newest: string; sample: { type: string; date: string; num: string; open: number; advantage: string }[] }[];
  };
  columns: string[];
  notes: string[];
}

// 3.18.15 — undo the conversion of a cutoff exception day
export interface UnconvertPlan {
  day: string;
  exceptionDay: boolean;
  cutoff?: string | null;
  offsets: { key: string; qboId: string; action: string; at?: string | null }[];
  documents: { invoice: string; customer: string; total: number; open: number; qboId: string; qboType: string; action: string; at?: string | null }[];
  openTotal: number;
  fullTotal: number;
  notes: string[];
}

// 3.18.14 — Desktop-migration cleanup plan
export interface CleanupPlan {
  cutoff: string;
  placeholderName: string;
  placeholderId?: string | null;
  placeholderBalance: number;
  customers: number;
  clearTotal: number;
  staleDocumentsTotal: number;
  staleLumpsTotal: number;
  expectedPlaceholderAfter: number;
  rows: {
    qboId: string; name: string; balance: number; clear: number; afterBalance: number;
    staleDocuments: number; staleLumps: number; staleDocumentsOpen: number; staleLumpsOpen: number;
    alreadyCleared: boolean;
    journalOpen?: number; note?: string | null;   // 3.23.2: open MIGR journal already covering part of the items
    items: { id: string; type: string; date: string; num: string; open: number; why: string; advantage?: string; matched?: boolean }[];   // 3.19.5: Advantage evidence
  }[];
  covered?: { qboId: string; name: string; items: number; journalOpen: number; itemCount: number }[];   // 3.23.2: cleared, journal still open next to the items
  notes: string[];
}

// 3.23.2 — undo the last cleanup run of a customer (duplicate MIGR journal)
export interface CleanupUndoRow {
  qboId: string; seq: number; docNumber: string; journalId: string; date: string; amount: number;
  paymentId?: string | null; previousDocNumber?: string | null; previousJournalId?: string | null; error?: string | null;
}

export const money = (v: number | null | undefined) =>
  (v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** ISO date n days after an ISO date (n may be negative). */
export const isoPlusDays = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

export const isoDaysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// 3.23.0 — re-sync a customer to Advantage (Advantage is the source of truth)
export interface ResyncDoc {
  invoice: string; type: string; date: string; total: number; open: number; stub?: number | null; delta: number;
  qboId?: string | null; qboType?: string | null; action: 'convert' | 'rewrite' | 'delete' | 'link' | 'cannot' | 'ok' | string; note?: string | null;
}
export interface ResyncItem { key: string; type: string; date: string; amount: number; action: string; qboId?: string | null; note?: string | null }
export interface ResyncPlan {
  code: number; name: string; qboCustomerId?: string | null; qboName: string; cutoff: string;
  adsOpenNow: number; adsBalanceField: number; qboBalance: number; openAtCutoff: number; stubsNow: number;
  convert: number; rewrite: number; delete: number; link: number; cannot: number; unchanged: number; netDelta: number;
  documents: ResyncDoc[]; postCutoff: ResyncItem[]; postCutoffAmount: number; receipts: ResyncItem[]; receiptsAmount: number;
  strays: ResyncItem[]; straysAmount: number; expectedAfter: number; notes: string[];
  sharedWith?: number[]; expectedAfterCombined?: number; postDated?: number; postDatedAmount?: number;   // 3.23.1
}
